import React, { Fragment, memo, useCallback } from 'react';
import { KatoSelectTrigger, Select, SelectOption } from '@app/components';
import { IconCloseMark, IconEdit16, IconPlusCircle24 } from '@app/icons';
import {
  Kato,
  UserFamilyStatus,
  UserFamilyStatusNames,
  UserGender,
  UserGenderName,
  UserPhone,
} from '@app/models';
import {
  mapRecordToSelectOption,
  formatPhoneNumber,
  getRawPhoneNumber,
} from '@app/helpers';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';

export interface UserInfoData {
  lastName: string;
  firstName: string;
  middleName: string;
  phone: string;
  gender: SelectOption<UserGender> | null;
  address: string;
  instagram: string;
  telegram: string;
  whatsAppNumber: string | null;
  phones: UserPhone[];
  iin: string;
  kato: Kato | null;
  familyStatus: SelectOption<UserFamilyStatus> | null;
  password: string | null;
  oldPassword: string | null;
  confirmPassword: string | null;
}

interface UserInfoFormProps {
  values: UserInfoData;
  errors: any;
  onChange: (value: any, name?: string) => void;
  onChangePassword?: () => void;
}

function UserInfoForm(props: UserInfoFormProps) {
  const { values, onChange, errors, onChangePassword } = props;

  const onChangePhones = useCallback(
    (index: number) => (value: string) => {
      const digitsOnly = getRawPhoneNumber(value);
      onChange(
        values.phones.map((prevPhone, phoneIndex) => ({
          ...prevPhone,
          phone: phoneIndex === index ? digitsOnly : prevPhone.phone,
        })),
        'phones'
      );
    },
    [onChange, values.phones]
  );

  const onClickAddPhone = useCallback(() => {
    onChange(
      [
        ...values.phones,
        {
          phone: '',
          isPrefer: false,
        },
      ],
      'phones'
    );
  }, [values.phones, onChange]);

  const onClickEditPassword = useCallback(() => {
    if (onChangePassword) {
      onChangePassword();
    }
  }, [onChangePassword]);

  const onCheckPreferred = useCallback(
    (index: number) => () => {
      onChange(
        values.phones.map((prevPhone, phoneIndex) => ({
          ...prevPhone,
          isPrefer: phoneIndex === index,
        })),
        'phones'
      );
    },
    [onChange, values.phones]
  );

  const onDeletePhone = useCallback(
    (index: number) => {
      onChange(
        values.phones.filter((_, phoneIndex) => phoneIndex !== index),
        'phones'
      );
    },
    [onChange, values.phones]
  );

  const onChangeKato = useCallback(
    (data: SelectOption<Kato>) => {
      onChange(data.value, 'kato');
    },
    [onChange]
  );

  const onChangeWhatsApp = useCallback(
    (value: string) => {
      const digitsOnly = getRawPhoneNumber(value);
      onChange(digitsOnly, 'whatsAppNumber');
    },
    [onChange]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div data-field="lastName">
          <Label htmlFor="lastName">Фамилия</Label>
          <Input
            id="lastName"
            onChange={(e) => onChange(e.target.value, 'lastName')}
            name="lastName"
            value={values.lastName}
            placeholder="Введите Фамилию"
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.lastName || ''}
          </span>
        </div>
        <div data-field="firstName">
          <Label htmlFor="firstName">Имя</Label>
          <Input
            id="firstName"
            onChange={(e) => onChange(e.target.value, 'firstName')}
            name="firstName"
            value={values.firstName}
            placeholder="Введите имя"
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.firstName || ''}
          </span>
        </div>
        <div data-field="middleName">
          <Label htmlFor="middleName">Отчество</Label>
          <Input
            id="middleName"
            onChange={(e) => onChange(e.target.value, 'middleName')}
            name="middleName"
            value={values.middleName}
            placeholder="Введите отчество"
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.middleName || ''}
          </span>
        </div>
        <div data-field="iin">
          <Label htmlFor="iin">ИИН</Label>
          <Input
            id="iin"
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, '');
              onChange(digitsOnly, 'iin');
            }}
            name="iin"
            value={values.iin}
            placeholder="Введите ИИН"
            maxLength={12}
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.iin || ''}
          </span>
        </div>
        <div data-field="gender">
          <Label htmlFor="gender">Выберите пол</Label>
          <Select
            label=""
            value={values.gender}
            placeholder="Выберите пол"
            options={[UserGender.MALE, UserGender.FAMALE].map((gender) =>
              mapRecordToSelectOption(gender, UserGenderName)
            )}
            name="gender"
            onChange={onChange}
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.gender || ''}
          </span>
        </div>
        <div data-field="familyStatus">
          <Label htmlFor="familyStatus">Семейное положение</Label>
          <Select
            label=""
            value={values.familyStatus}
            placeholder="Выберите семейное положение"
            options={[
              UserFamilyStatus.NOT_MARRIED,
              UserFamilyStatus.MARRIED,
            ].map((familyStatus) =>
              mapRecordToSelectOption(familyStatus, UserFamilyStatusNames)
            )}
            name="familyStatus"
            onChange={onChange}
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.familyStatus || ''}
          </span>
        </div>
        <div>
          <Label htmlFor="phone">Номер телефона (Основной)</Label>
          <Input
            id="phone"
            value={values.phone}
            placeholder="Введите ИИН"
            disabled
          />
        </div>
      </div>
      <div className="space-y-4" data-field="phones">
        {values.phones.map((phone, phoneIndex) => (
          <div
            key={phoneIndex}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center"
          >
            <div>
              <Label htmlFor={`phone-${phoneIndex}`} className="mb-2 block">
                Дополнительный номер телефона {phoneIndex + 1}
              </Label>
              <Input
                id={`phone-${phoneIndex}`}
                onChange={(e) => onChangePhones(phoneIndex)(e.target.value)}
                value={formatPhoneNumber(phone.phone)}
                placeholder="(777) 777 77 77"
              />
              <span className="block min-h-[16px] text-red-500 text-xs mt-1">
                {errors[`phones[${phoneIndex}].phone`] || ''}
              </span>
            </div>
            <div className="flex h-10 w-full items-center gap-3">
              <Checkbox
                id={`prefer-${phoneIndex}`}
                checked={phone.isPrefer}
                onCheckedChange={onCheckPreferred(phoneIndex)}
              />
              <Label
                htmlFor={`prefer-${phoneIndex}`}
                className="cursor-pointer text-sm m-0"
              >
                Предпочитаемый способ связи
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-auto !bg-transparent !border-none !shadow-none text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                onClick={() => onDeletePhone(phoneIndex)}
                title="Удалить номер"
              >
                <IconCloseMark />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          className="mt-2 flex items-center gap-2"
          onClick={onClickAddPhone}
          disabled={values.phones.length >= 3}
        >
          <IconPlusCircle24 /> Добавить дополнительный номер
          {values.phones.length >= 3 && ' (макс. 3)'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="mt-2 flex items-center gap-2"
          onClick={onClickEditPassword}
        >
          <IconEdit16 />{' '}
          {values.password === null
            ? 'Изменить пароль'
            : 'Сохранить новый пароль'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-field="address">
          <Label htmlFor="address">Адрес</Label>
          <Input
            id="address"
            onChange={(e) => onChange(e.target.value, 'address')}
            name="address"
            value={values.address}
            placeholder="Введите адрес"
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.address || ''}
          </span>
        </div>
        <div>
          <Label htmlFor="kato">Регион</Label>
          <KatoSelectTrigger
            onChange={onChangeKato}
            label=""
            name="kato"
            value={(values.kato ? values.kato.nameRu : '') || undefined}
            placeholder="Выберите регион"
            helperText={errors.kato || undefined}
            error={!!errors.kato}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div data-field="whatsAppNumber">
          <Label htmlFor="whatsAppNumber">Номер WhatsApp</Label>
          <Input
            id="whatsAppNumber"
            onChange={(e) => onChangeWhatsApp(e.target.value)}
            name="whatsAppNumber"
            value={formatPhoneNumber(values.whatsAppNumber || '')}
            placeholder="(777) 777 77 77"
          />
          <span className="block min-h-[16px] text-red-500 text-xs mt-1">
            {errors.whatsAppNumber || ''}
          </span>
        </div>
        <div>
          <Label htmlFor="instagram">instagram</Label>
          <Input
            id="instagram"
            onChange={(e) => onChange(e.target.value, 'instagram')}
            name="instagram"
            value={values.instagram || ''}
            placeholder="Введите instagram"
          />
        </div>
        <div>
          <Label htmlFor="telegram">telegram</Label>
          <Input
            id="telegram"
            onChange={(e) => onChange(e.target.value, 'telegram')}
            name="telegram"
            value={values.telegram || ''}
            placeholder="Введите telegram"
          />
        </div>
      </div>
      {values.password !== null && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="oldPassword">Старый пароль</Label>
            <Input
              id="oldPassword"
              onChange={(e) => onChange(e.target.value, 'oldPassword')}
              type="password"
              value={values.oldPassword || ''}
              placeholder="ХХХХХХХХХХХ"
              name="oldPassword"
              autoComplete="off"
            />
            {errors.oldPassword && (
              <span className="text-red-500 text-xs mt-1">
                {errors.oldPassword}
              </span>
            )}
          </div>
          <div>
            <Label htmlFor="password">Новый пароль</Label>
            <Input
              id="password"
              onChange={(e) => onChange(e.target.value, 'password')}
              type="password"
              value={values.password || ''}
              placeholder="ХХХХХХХХХХХ"
              name="password"
              autoComplete="off"
            />
            {errors.password && (
              <span className="text-red-500 text-xs mt-1">
                {errors.password}
              </span>
            )}
          </div>
          <div>
            <Label htmlFor="confirmPassword">Подтверждение пароля</Label>
            <Input
              id="confirmPassword"
              onChange={(e) => onChange(e.target.value, 'confirmPassword')}
              type="password"
              value={values.confirmPassword || ''}
              placeholder="ХХХХХХХХХХХ"
              name="confirmPassword"
              autoComplete="off"
            />
            {errors.confirmPassword && (
              <span className="text-red-500 text-xs mt-1">
                {errors.confirmPassword}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(UserInfoForm);
